// Third-party
import React from 'react';

// Components
import { Filter } from '@/views/components/inputs';

// Utils
import { FormattedMessage } from '@/utils/i18n';

// Types
import FilterType from '@/types/filter-type';
import DropdownItem from '@/types/dropdown-item';

type Props = {
  filters: FilterType[];
  onFilterChange: (value: unknown, filter: FilterType) => void;
  onClearAll: () => void;
  loadingFilters?: boolean;
  activeFiltersCount?: number;
  activeFilters?: Record<string, unknown>;
};

/**
 * Renders filter dropdowns without search input/button.
 * Used in screener to show only the filter controls.
 */
const FilterControls = ({ filters, onFilterChange, onClearAll, loadingFilters, activeFiltersCount = 0, activeFilters = {} }: Props) => {
  const filterRefs = React.useRef<Array<{ clearFilter: () => void }>>([]);

  const setFilterRef = (ref: { clearFilter: () => void } | null) => {
    if (ref && !filterRefs.current.includes(ref)) {
      filterRefs.current.push(ref);
    }
  };

  const clearAllFilters = () => {
    filterRefs.current.forEach(ref => {
      if (ref && ref.clearFilter) {
        ref.clearFilter();
      }
    });
    onClearAll();
  };

  const hasActiveFilters = (): boolean => activeFiltersCount > 0;

  const getFilterValue = (filter: FilterType) => {
    const rawValue = activeFilters[filter.key];
    if (rawValue === undefined || rawValue === null) {
      return undefined;
    }

    // Handle range filters: [min, max] arrays with numeric values
    if (Array.isArray(rawValue) && rawValue.length === 2 && rawValue.every(v => v === null || typeof v === 'number')) {
      // Find the matching option by comparing array values
      const matchedOption = filter.options.find(opt => {
        const optValue = opt.value;
        return Array.isArray(optValue) && optValue.length === 2 && optValue[0] === rawValue[0] && optValue[1] === rawValue[1];
      });
      return matchedOption ? [matchedOption] : undefined;
    }

    // Convert stored values back to FilterOption format for discrete options
    if (Array.isArray(rawValue)) {
      return rawValue.map(val => filter.options.find(opt => opt.value === val)).filter(Boolean);
    }

    const matchedOption = filter.options.find(opt => opt.value === rawValue);
    return matchedOption ? [matchedOption] : undefined;
  };

  return (
    <div className="screener-filter-controls">
      <div className="search-filters-container">
        <div className="d-flex flex-wrap search-filters">
          {filters.map((filter, i) => (
            <Filter
              key={filter.key}
              ref={setFilterRef}
              onChange={value => {
                onFilterChange(value, filter);
              }}
              multiselect={!!filter.multiselect}
              search={filter.search}
              optionsGroups={[
                {
                  options: filter.options,
                },
              ]}
              loading={loadingFilters}
              tooltip={filter.tooltip}
              label={filter.label}
              value={getFilterValue(filter) as DropdownItem[]}
            />
          ))}

          {hasActiveFilters() && (
            <button type="button" className="clear-filters-button" onClick={clearAllFilters}>
              <i className="icon-ds-close clear-filters-icon" />
              <span className="clear-filters-text">
                <FormattedMessage id="Screener.Filter.Clear" />
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterControls;
